import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';

interface Shift {
  id: string;  // Fixed: should be string not number (SHIFT_1, SHIFT_2)
  shift: number;  // Changed from shiftNumber to match API
  shiftName: string;  // Added to match API response
  startTime: string;
  endTime: string;
  isActive?: boolean;  // Added to match API
  updatedAt?: string;
}

interface TouchTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
}

const TouchTimePicker: React.FC<TouchTimePickerProps> = ({ value, onChange, label }) => {
  const [hours, setHours] = useState('08');
  const [minutes, setMinutes] = useState('00');

  useEffect(() => {
    const [h, m] = value.split(':');
    setHours(h.padStart(2, '0'));
    setMinutes(m.padStart(2, '0'));
  }, [value]);

  const handleTimeChange = (newHours: string, newMinutes: string) => {
    setHours(newHours);
    setMinutes(newMinutes);
    onChange(`${newHours}:${newMinutes}`);
  };

  const adjustHours = (delta: number) => {
    const newHours = Math.max(0, Math.min(23, parseInt(hours) + delta));
    handleTimeChange(newHours.toString().padStart(2, '0'), minutes);
  };

  const adjustMinutes = (delta: number) => {
    const newMinutes = Math.max(0, Math.min(59, parseInt(minutes) + delta));
    handleTimeChange(hours, newMinutes.toString().padStart(2, '0'));
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border">
      <label className="block text-sm font-semibold text-gray-700 mb-3">{label}</label>
      <div className="flex items-center justify-center space-x-4">
        {/* Hours */}
        <div className="flex flex-col items-center">
          <button
            onClick={() => adjustHours(1)}
            className="w-12 h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xl font-bold shadow-md active:scale-95 transition-all"
          >
            +
          </button>
          <div className="w-16 h-16 bg-white border-2 border-gray-300 rounded-lg flex items-center justify-center my-2">
            <span className="text-3xl font-bold text-gray-800">{hours}</span>
          </div>
          <button
            onClick={() => adjustHours(-1)}
            className="w-12 h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xl font-bold shadow-md active:scale-95 transition-all"
          >
            −
          </button>
        </div>

        {/* Colon */}
        <div className="text-3xl font-bold text-gray-800">:</div>

        {/* Minutes */}
        <div className="flex flex-col items-center">
          <button
            onClick={() => adjustMinutes(15)}
            className="w-12 h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xl font-bold shadow-md active:scale-95 transition-all"
          >
            +
          </button>
          <div className="w-16 h-16 bg-white border-2 border-gray-300 rounded-lg flex items-center justify-center my-2">
            <span className="text-3xl font-bold text-gray-800">{minutes}</span>
          </div>
          <button
            onClick={() => adjustMinutes(-15)}
            className="w-12 h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xl font-bold shadow-md active:scale-95 transition-all"
          >
            −
          </button>
        </div>
      </div>
    </div>
  );
};


const ShiftsSettings: React.FC = () => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string>('');
  
  const apiUrl = getApiUrl();

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      const response = await fetch(`${apiUrl}/shifts`);
      const result = await response.json();
      if (result.data) {
        setShifts(result.data);
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
    }
  };

  const updateShift = async (id: string, startTime: string, endTime: string) => {
    setLoading(true);
    setSaveStatus('');
    try {
      const response = await fetch(`${apiUrl}/shifts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startTime, endTime }),
      });
      
      const result = await response.json();
      if (result.message === 'success') {
        await fetchShifts();
        // Don't show success message - only show errors
      } else {
        setSaveStatus(`Error: ${result.error}`);
        setTimeout(() => setSaveStatus(''), 5000);
      }
    } catch (error) {
      console.error('Error updating shift:', error);
      setSaveStatus('Failed to update shift');
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const isOvernightShift = (startTime: string, endTime: string) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const startMinuteTime = startHours * 60 + startMinutes;
    const endMinuteTime = endHours * 60 + endMinutes;
    return endMinuteTime < startMinuteTime;
  };

  const calculateDuration = (startTime: string, endTime: string) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const startMinuteTime = startHours * 60 + startMinutes;
    const endMinuteTime = endHours * 60 + endMinutes;
    
    let durationMinutes;
    if (endMinuteTime < startMinuteTime) {
      // Overnight shift
      durationMinutes = (24 * 60) - startMinuteTime + endMinuteTime;
    } else {
      durationMinutes = endMinuteTime - startMinuteTime;
    }
    
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    return { hours, minutes };
  };

  const updateShiftStartTime = (shiftId: string, startTime: string) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (shift) {
      updateShift(shiftId, startTime, shift.endTime);
    }
  };

  const updateShiftEndTime = (shiftId: string, endTime: string) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (shift) {
      updateShift(shiftId, shift.startTime, endTime);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Shift Configuration</h2>
        
        {saveStatus && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700 border border-red-200">
            {saveStatus}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {shifts.map((shift) => {
            const isOvernight = isOvernightShift(shift.startTime, shift.endTime);
            const duration = calculateDuration(shift.startTime, shift.endTime);
            return (
              <div key={shift.id} className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                  {shift.shiftName}
                </h3>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-700 mb-2">Shift Summary</h4>
                      <div className="text-lg">
                        <span className="font-bold text-blue-700">
                          {shift.startTime} - {shift.endTime}
                        </span>
                        {isOvernight && (
                          <span className="text-sm text-blue-600 ml-2">(next day)</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Duration: {duration.hours}h {duration.minutes > 0 ? `${duration.minutes}m` : ''}
                      </div>
                    </div>
                  </div>
                  
                  <TouchTimePicker
                    value={shift.startTime}
                    onChange={(time) => updateShiftStartTime(shift.id, time)}
                    label="Start Time"
                  />
                  
                  <TouchTimePicker
                    value={shift.endTime}
                    onChange={(time) => updateShiftEndTime(shift.id, time)}
                    label="End Time"
                  />
                </div>
                
                {loading && (
                  <div className="mt-4 text-center">
                    <div className="inline-flex items-center px-4 py-2 text-sm text-blue-600">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ShiftsSettings;